import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../config';

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const api = {
  list: (params) =>
    axios.get(`${API_URL}/api/email-rules`, {
      params,
      headers: getAuthHeader(),
    }).then((r) => r.data),

  get: (id) =>
    axios.get(`${API_URL}/api/email-rules/${id}`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  create: (data) =>
    axios.post(`${API_URL}/api/email-rules`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  update: (id, data) =>
    axios.put(`${API_URL}/api/email-rules/${id}`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  toggleActive: (id) =>
    axios.post(`${API_URL}/api/email-rules/${id}/toggle-active`, {}, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  test: (id, context) =>
    axios.post(`${API_URL}/api/email-rules/${id}/test`, { context }, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  eventTypes: () =>
    axios.get(`${API_URL}/api/email-rules/event-types`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),
};

export function useRules(filters = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-rules', filters],
    queryFn: () => api.list(filters),
  });

  return {
    rules: data?.data || [],
    total: data?.total || 0,
    isLoading,
    error,
  };
}

export function useRule(id) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-rule', id],
    queryFn: () => api.get(id),
    enabled: !!id,
  });

  return {
    rule: data?.data || null,
    isLoading,
    error,
  };
}

export function useCreateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-rules'] }),
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-rules'] });
      queryClient.invalidateQueries({ queryKey: ['email-rule', variables.id] });
    },
  });
}

export function useToggleRuleActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.toggleActive(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-rules'] }),
  });
}

export function useTestRule() {
  return useMutation({
    mutationFn: ({ id, context }) => api.test(id, context),
  });
}

export function usePauseRuleJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId) => {
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}/pause-jobs`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to pause rule');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-rules'] });
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    },
  });
}

export function useEventTypes() {
  const { data, isLoading } = useQuery({
    queryKey: ['email-event-types'],
    queryFn: () => api.eventTypes(),
    staleTime: 5 * 60 * 1000,
  });

  return { eventTypes: data?.data || [], isLoading };
}

export { api as ruleApi };
